/// <amd-module name="VSSUI/Components/PickList/ItemPicker" />

import * as React from "react";
import { BaseComponent, IBaseProps } from "OfficeFabric/Utilities";
import { css } from "VSSUI/Util";
import {
    IItemPickerDropdown,
    IItemPickerDropdownProps,
    IItemPickList,
    IItemPickListProps,
    IPickList,
    IPickListDropdown,
    IItemPickerProvider
} from "./PickList.Props";
import { PickList } from "./PickList";
import { PickListDropdown } from "./PickListDropdown";

/** @deprecated */
export const PICKER_CHANGE_EVENT = "picker-changed";

/** @deprecated */
export interface IItemPickerBaseProps extends IBaseProps {
    provider: IItemPickerProvider;
}

/** @deprecated */
export abstract class ItemPickerComponentBase<TProps extends IItemPickerBaseProps> extends BaseComponent<TProps> {
    private updateDelegate: (args?: any) => void;
    public pickListDropdown: IPickListDropdown;

    constructor(props: TProps) {
        super(props);
        this.updateDelegate = () => this.forceUpdate();

        this.state = {};
    }

    public componentDidMount() {
        this._events.on(this.props.provider, PICKER_CHANGE_EVENT, this.updateDelegate);
    }

    public componentWillUnmount() {
        this._events.off(this.props.provider, PICKER_CHANGE_EVENT, this.updateDelegate);
    }

    public componentWillReceiveProps(nextProps: IItemPickerDropdownProps) {
        if (this.props.provider !== nextProps.provider) {
            this._events.off(this.props.provider, PICKER_CHANGE_EVENT, this.updateDelegate);
            this._events.on(nextProps.provider, PICKER_CHANGE_EVENT, this.updateDelegate);
        }
    }
}

/** @deprecated */
export class ItemPickerDropdown extends ItemPickerComponentBase<IItemPickerDropdownProps> implements IItemPickerDropdown {
    public pickListDropdown: IPickListDropdown;

    public render(): JSX.Element | null {
        const {
            className,
            provider,
            minWidth,
            pickListWidth,
            pickListClassName,
            tabIndex,
            placeholderText,
            hideSelectedItemIcon,
            dropdownIconProps,
            autoFocus,
            onFocus,
            onFocusLost,
            onRenderSelectedItemText,
            titleTooltipFormat,
        } = this.props;

        return (
            <PickListDropdown
                id={this.props.id}
                componentRef={pickListDropdown => (this.pickListDropdown = pickListDropdown)}
                className={css("item-picker-dropdown", className)}
                getListItem={provider.getListItem}
                getPickListItems={() => {
                    return provider.getItems();
                }}
                onSelectionChanged={selection => {
                    return provider.onSelectedItemChanged
                        ? provider.onSelectedItemChanged(selection && selection.selectedItems ? selection.selectedItems[0] : undefined)
                        : false;
                }}
                selectedItems={provider.selectedItem ? [provider.selectedItem] : undefined}
                indicators={provider.dropdownIndicators}
                pickListClassName={pickListClassName}
                selectedItemIndicators={provider.selectedItemIndicators}
                pivots={provider.pivots}
                groups={provider.groups}
                limitSearchToPivot={provider.limitSearchToPivot}
                onGroupLoadRequest={provider.onGroupLoadRequest}
                placeholder={placeholderText}
                ariaLabelFormat={provider.ariaLabelFormat}
                isSearchable={provider.isSearchable}
                minItemsForSearchBox={provider.minItemsForSearchBox}
                searchTextPlaceholder={provider.searchTextPlaceholder}
                searchResultsGroupName={provider.searchResultsGroupName}
                searchResultsLoadingText={provider.searchResultsLoadingText}
                searchNoResultsText={provider.searchNoResultsText}
                onSearch={provider.onSearch}
                onHasMoreSearchResults={provider.onHasMoreSearchResults}
                onFilterItemByText={provider.onFilterItemByText}
                titleTooltipFormat={titleTooltipFormat}
                getActions={items => provider.actions}
                noItemsText={provider.noItemsText}
                selectExactMatchOnEnter={provider.selectExactMatchOnEnter}
                width={minWidth}
                pickListWidth={pickListWidth}
                tabIndex={tabIndex}
                hideSelectedItemIcon={hideSelectedItemIcon}
                dropdownIconProps={dropdownIconProps}
                autoFocus={autoFocus}
                shouldVirtualize={items => {
                    return (items || []).length > 50;
                }}
                onRenderItemText={this.props.onRenderItemText}
                isOpen={this.props.isOpen}
                onToggleDropdown={this.props.onToggleDropdown}
                ariaDescribedBy={this.props.ariaDescribedBy}
                onFocus={onFocus}
                onFocusLost={onFocusLost}
                onRenderSelectedItemText={onRenderSelectedItemText}
            />
        );
    }

    public focus(): void {
        if (this.pickListDropdown) {
            this.pickListDropdown.focus();
        }
    }
}

/** @deprecated */
export class ItemPickList extends ItemPickerComponentBase<IItemPickListProps> implements IItemPickList {
    public pickList: IPickList;

    public render(): JSX.Element | null {
        const { className, provider, useDropdownStyle, shouldVirtualize } = this.props;

        return (
            <PickList
                componentRef={pickList => (this.pickList = pickList)}
                className={css(useDropdownStyle ? "item-picker-dropdown" : "item-picker-list", className)}
                getListItem={provider.getListItem}
                items={provider.getItems()}
                onSelectionChanged={selection => {
                    return provider.onSelectedItemChanged
                        ? provider.onSelectedItemChanged(selection && selection.selectedItems ? selection.selectedItems[0] : undefined)
                        : false;
                }}
                selectedItems={provider.selectedItem ? [provider.selectedItem] : undefined}
                indicators={provider.dropdownIndicators}
                groups={provider.groups}
                pivots={provider.pivots}
                limitSearchToPivot={provider.limitSearchToPivot}
                onGroupLoadRequest={provider.onGroupLoadRequest}
                isSearchable={provider.isSearchable}
                searchTextPlaceholder={provider.searchTextPlaceholder}
                searchResultsGroupName={provider.searchResultsGroupName}
                searchResultsLoadingText={provider.searchResultsLoadingText}
                searchNoResultsText={provider.searchNoResultsText}
                onSearch={provider.onSearch}
                onHasMoreSearchResults={provider.onHasMoreSearchResults}
                onFilterItemByText={provider.onFilterItemByText}
                getActions={items => provider.actions}
                noItemsText={provider.noItemsText}
                selectExactMatchOnEnter={provider.selectExactMatchOnEnter}
                shouldVirtualize={
                    shouldVirtualize
                        ? shouldVirtualize
                        : items => {
                              return (items || []).length > 50;
                          }
                }
                minItemsForSearchBox={useDropdownStyle ? undefined : 1}
                onRenderItemText={this.props.onRenderItemText}
            />
        );
    }
}
